import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nProvider";
import { useCart } from "@/contexts/CartContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { LogoIcon } from "@/components/Logo";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, ShoppingBag, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currencySymbol = (code) => (code === "EUR" ? "€" : code === "USD" ? "$" : code === "GBP" ? "£" : code + " ");
const formatPrice = (amount, code = "EUR") => {
  const num = Number(amount).toFixed(2);
  const sym = currencySymbol(code);
  return code === "EUR" || code === "GBP" ? `${num} ${sym}` : `${sym}${num}`;
};

export default function ProductDetail() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // selectedOptions: { [optionName]: value }
  const [selected, setSelected] = useState({});
  const [imageIdx, setImageIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    window.scrollTo({ top: 0, behavior: "instant" });
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/products/${handle}`);
        if (cancelled) return;
        setProduct(res.data);
        // Default: first available variant's options
        const firstAvailable =
          res.data.variants?.find((v) => v.availableForSale) || res.data.variants?.[0];
        if (firstAvailable) {
          const init = {};
          (firstAvailable.selectedOptions || []).forEach((o) => {
            init[o.name] = o.value;
          });
          setSelected(init);
        }
        setImageIdx(0);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "notfound" : "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [handle]);

  // Find matching variant from selected options
  const currentVariant = useMemo(() => {
    if (!product) return null;
    if (!product.options?.length) return product.variants?.[0] || null;
    return (
      product.variants?.find((v) =>
        (v.selectedOptions || []).every((o) => selected[o.name] === o.value)
      ) || null
    );
  }, [product, selected]);

  const images = product?.images?.length ? product.images : (product?.featuredImage ? [product.featuredImage] : []);

  const handleSelect = (optionName, value) => {
    setSelected((p) => ({ ...p, [optionName]: value }));
  };

  const handleAddToCart = () => {
    if (!currentVariant) {
      toast.error(t.pdp.selectVariant);
      return;
    }
    if (!currentVariant.availableForSale) {
      toast.error(t.products.soldOut);
      return;
    }
    addItem({
      variantId: currentVariant.id,
      productHandle: product.handle,
      title: product.title,
      variantTitle: currentVariant.title,
      price: Number(currentVariant.price?.amount || 0),
      currency: currentVariant.price?.currencyCode || "EUR",
      image: images[0]?.url || null,
      quantity: 1,
    });
    toast.success(t.pdp.addedToCart);
  };

  // ===== Render states =====
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white" data-testid="pdp-loading">
        <Header />
        <div className="flex items-center justify-center min-h-[70vh]">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-[#050505] text-white" data-testid="pdp-error">
        <Header />
        <div className="max-w-2xl mx-auto px-6 pt-40 pb-32 text-center">
          <LogoIcon size={48} color="#D4AF37" />
          <h1 className="mt-6 font-display text-4xl uppercase">{t.pdp.notFoundTitle}</h1>
          <p className="mt-4 font-body text-white/55">{t.pdp.notFoundBody}</p>
          <Link
            to="/"
            data-testid="pdp-back-home"
            className="inline-flex items-center gap-2 mt-8 px-6 py-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors font-display text-sm tracking-[0.2em] uppercase"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.pdp.backShop}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const mainImage = images[imageIdx] || images[0];
  const variantPrice = currentVariant?.price?.amount || product.price;
  const variantCurrency = currentVariant?.price?.currencyCode || product.currency;
  const compareAt = currentVariant?.compareAtPrice;

  // SEO Meta
  const ogImage = images[0]?.url || "https://kirchero.store/og-image.png";
  const productUrl = `https://kirchero.store/product/${product.handle}`;
  const cleanDescription = (product.description || product.title || "")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
  const inStock = !!currentVariant?.availableForSale;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description: cleanDescription,
    image: images.slice(0, 6).map((i) => i.url).filter(Boolean),
    sku: currentVariant?.sku || product.handle,
    brand: { "@type": "Brand", name: "KirchHero" },
    offers: {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: variantCurrency,
      price: variantPrice,
      availability: inStock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="pdp-page">
      <Helmet>
        <title>{`${product.title} — KirchHero`}</title>
        <meta name="description" content={cleanDescription} />
        <link rel="canonical" href={productUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.title} — KirchHero`} />
        <meta property="og:description" content={cleanDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={productUrl} />
        <meta property="product:price:amount" content={String(variantPrice)} />
        <meta property="product:price:currency" content={variantCurrency} />
        <meta property="product:availability" content={inStock ? "in stock" : "out of stock"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.title} — KirchHero`} />
        <meta name="twitter:description" content={cleanDescription} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* Breadcrumb */}
      <div className="pt-28 pb-4 max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="pdp-back"
          className="inline-flex items-center gap-2 font-body text-[11px] tracking-[0.25em] uppercase text-white/55 hover:text-[#D4AF37] transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t.pdp.back}
        </button>
      </div>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Gallery */}
        <div className="lg:col-span-7 space-y-4">
          <div className="relative aspect-[4/5] bg-[#11111a] overflow-hidden group">
            {mainImage ? (
              <img
                key={mainImage.url}
                src={mainImage.url}
                alt={mainImage.altText || product.title}
                className="w-full h-full object-cover kh-fade-up"
                data-testid="pdp-main-image"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/20">
                <LogoIcon size={64} color="#1f1f29" />
              </div>
            )}

            {/* Item No. — magazine style */}
            <div className="absolute top-4 left-4 flex items-baseline gap-1">
              <span className="font-serif-luxury italic text-[#B5651D] text-sm">No.</span>
              <span className="font-display text-lg text-[#D4AF37]">
                {String(imageIdx + 1).padStart(2, "0")}
              </span>
              <span className="font-body text-[10px] tracking-[0.25em] uppercase text-white/40 ml-2">
                / {String(images.length).padStart(2, "0")}
              </span>
            </div>

            {/* Nav arrows */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setImageIdx((i) => (i - 1 + images.length) % images.length)}
                  data-testid="pdp-prev-image"
                  aria-label="Previous image"
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 backdrop-blur border border-white/10 text-white opacity-0 group-hover:opacity-100 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setImageIdx((i) => (i + 1) % images.length)}
                  data-testid="pdp-next-image"
                  aria-label="Next image"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 backdrop-blur border border-white/10 text-white opacity-0 group-hover:opacity-100 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
              {images.slice(0, 12).map((img, i) => (
                <button
                  key={img.url + i}
                  type="button"
                  onClick={() => setImageIdx(i)}
                  data-testid={`pdp-thumb-${i}`}
                  aria-label={`Image ${i + 1}`}
                  className={`relative aspect-square overflow-hidden border transition-all ${
                    i === imageIdx
                      ? "border-[#D4AF37]"
                      : "border-[#1f1f29] opacity-60 hover:opacity-100 hover:border-white/30"
                  }`}
                >
                  <img src={img.url} alt={img.altText || ""} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Buy block */}
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-[#A93043]" />
            <span className="kh-chapter">{product.productType || "KirchHero"}</span>
          </div>

          <h1
            data-testid="pdp-title"
            className="font-display text-4xl sm:text-5xl uppercase leading-[1.05] text-white"
          >
            {product.title}
          </h1>

          {/* Price */}
          <div className="mt-6 flex items-baseline gap-3 pb-6 border-b border-[#1f1f29]">
            <span data-testid="pdp-price" className="font-display text-3xl text-[#D4AF37]">
              {formatPrice(variantPrice, variantCurrency)}
            </span>
            {compareAt && Number(compareAt) > Number(variantPrice) && (
              <span className="font-body text-base text-white/40 line-through">
                {formatPrice(compareAt, variantCurrency)}
              </span>
            )}
            <span className="font-body text-[10px] tracking-[0.2em] uppercase text-white/40 ml-auto">
              {t.pdp.taxIncluded}
            </span>
          </div>

          {/* Options */}
          {product.options
            ?.filter((o) => o.values?.length && o.name !== "Title")
            .map((opt) => (
              <div key={opt.name} className="mt-7" data-testid={`pdp-option-${opt.name}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <span className="font-body text-[11px] tracking-[0.3em] uppercase text-white/55">
                    {opt.name}
                  </span>
                  <span className="font-serif-luxury italic text-[#D4AF37]/80 text-sm">
                    {selected[opt.name] || "—"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {opt.values.map((val) => {
                    const isSelected = selected[opt.name] === val;
                    // Is any variant with this value still in stock?
                    const isAvailable = product.variants?.some(
                      (v) =>
                        v.availableForSale &&
                        (v.selectedOptions || []).some((so) => so.name === opt.name && so.value === val)
                    );
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => handleSelect(opt.name, val)}
                        data-testid={`pdp-option-${opt.name}-${val}`}
                        disabled={!isAvailable}
                        className={`px-4 py-2.5 border font-body text-xs tracking-[0.18em] uppercase transition-all ${
                          isSelected
                            ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                            : isAvailable
                            ? "border-[#1f1f29] text-white hover:border-[#D4AF37]/60"
                            : "border-[#1f1f29] text-white/25 line-through cursor-not-allowed"
                        }`}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

          {/* Availability info */}
          <div className="mt-7 flex items-center gap-2">
            {currentVariant?.availableForSale ? (
              <>
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="font-body text-xs tracking-wider uppercase text-white/65">
                  {t.pdp.inStock}
                </span>
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full bg-[#A93043]" />
                <span className="font-body text-xs tracking-wider uppercase text-[#A93043]">
                  {t.products.soldOut}
                </span>
              </>
            )}
          </div>

          {/* CTA */}
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={!currentVariant?.availableForSale}
            data-testid="pdp-add-to-cart"
            className="mt-7 w-full flex items-center justify-center gap-3 py-4 bg-[#D4AF37] text-black font-display text-base tracking-[0.25em] uppercase hover:bg-[#A93043] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            {t.pdp.addToCart}
          </button>

          {/* Trust strip */}
          <ul className="mt-7 space-y-2.5 text-white/65 font-body text-sm">
            <li className="flex items-center gap-2.5">
              <Check className="w-4 h-4 text-[#D4AF37]" />
              <span>{t.pdp.feature1}</span>
            </li>
            <li className="flex items-center gap-2.5">
              <Check className="w-4 h-4 text-[#D4AF37]" />
              <span>{t.pdp.feature2}</span>
            </li>
            <li className="flex items-center gap-2.5">
              <Check className="w-4 h-4 text-[#D4AF37]" />
              <span>{t.pdp.feature3}</span>
            </li>
          </ul>

          {/* Description */}
          {product.descriptionHtml && (
            <div className="mt-10 pt-8 border-t border-[#1f1f29]">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-6 bg-[#A93043]" />
                <span className="kh-eyebrow">{t.pdp.description}</span>
              </div>
              <div
                data-testid="pdp-description"
                className="font-body text-sm text-white/70 leading-relaxed pdp-prose"
                dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
              />
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
